'use client';

import { clsx } from 'clsx';
import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useId,
  useState,
} from 'react';

import CoreIcon from '../icon/icon';
import {
  CoreAccordionEvent,
  CoreAccordionIconProps,
  CoreAccordionItemProps,
  CoreAccordionProps,
} from '../shared/types/accordion.types';

import styles from './accordion.module.scss';

const AccordionContext = createContext<CoreAccordionProps | undefined>(
  undefined
);

const CoreAccordion: React.FC<CoreAccordionProps> = ({
  collapseIcon = { name: 'icon-action-minus' },
  expandIcon = { name: 'icon-action-plus' },
  isFlushed = false,
  isTitleAccent = false,
  items,
  onSelectionChange,
  size = 'medium',
  titleElement = 'h3',
  children,
  lazyLoad = false,
  isSingleExpand = false,
}) => {
  const [coreAccordionItems, setCoreAccordionItems] =
    useState<CoreAccordionItemProps[]>(items);

  useEffect(() => {
    setCoreAccordionItems(items);
  }, [items]);

  const handleSelectionChange = (event: CoreAccordionEvent) => {
    if (onSelectionChange) {
      onSelectionChange(event);
    }
    setCoreAccordionItems((previousItems) => {
      const changeIndex = event.eventKey;
      const changedAccordion = previousItems[changeIndex];

      if (!changedAccordion) {
        return previousItems;
      }

      const isExpanding = !changedAccordion.isExpanded;

      if (isSingleExpand && isExpanding) {
        return previousItems.map((item, index) => ({
          ...item,
          isExpanded: index === changeIndex,
        }));
      }

      return previousItems.map((item, index) =>
        index === changeIndex ? { ...item, isExpanded: isExpanding } : item
      );
    });
  };

  const contextValue: CoreAccordionProps = {
    collapseIcon,
    expandIcon,
    isFlushed,
    isTitleAccent,
    items: coreAccordionItems,
    onSelectionChange: handleSelectionChange,
    size,
    titleElement,
    children,
    lazyLoad,
    isSingleExpand,
  };

  const classNamesCombined = clsx(
    styles['core-accordion-group'],
    styles[`core-accordion-group-${size}`],
    isTitleAccent && styles[`core-accordion-group-${size}-accent`],
    isFlushed && styles['core-accordion-group-flushed']
  );

  return (
    <AccordionContext.Provider value={contextValue}>
      <div className={classNamesCombined}>
        {coreAccordionItems.map((item, index) => (
          <CoreAccordionItem
            key={item.id ?? `${item.title}-${index}`}
            {...item}
            eventKey={index}
          />
        ))}
        {children}
      </div>
    </AccordionContext.Provider>
  );
};

const CoreAccordionItem: React.FC<CoreAccordionItemProps> = ({
  children,
  isExpanded = false,
  leadingIcon,
  onPress,
  title,
  eventKey,
}) => {
  const context = useContext(AccordionContext);
  const ariaContentSuffix = '-content';
  const ariaTriggerSuffix = '-trigger';

  const accordionGeneratedId = useId();
  const accordionItemId = `accordion-${accordionGeneratedId}`;

  const handleToggleContent = () => {
    const customEvent: CoreAccordionEvent = {
      eventKey: eventKey!,
      accordionIsExpanded: isExpanded,
      accordionContentId: `${accordionItemId}${ariaContentSuffix}`,
      accordionTriggerId: `${accordionItemId}${ariaTriggerSuffix}`,
      accordionTitle: title!,
    };

    if (onPress) {
      onPress(customEvent);
    }
    if (context?.onSelectionChange) {
      context.onSelectionChange(customEvent);
    }
  };

  return (
    <div className={styles['core-accordion-item']}>
      <CoreAccordionTrigger
        title={title}
        isExpanded={isExpanded}
        leadingIcon={leadingIcon}
        clickHandler={handleToggleContent}
        id={accordionItemId}
      />
      {context?.lazyLoad ? (
        isExpanded ? (
          <CoreAccordionContent isExpanded={isExpanded} id={accordionItemId}>
            {children}
          </CoreAccordionContent>
        ) : null
      ) : (
        <CoreAccordionContent isExpanded={isExpanded} id={accordionItemId}>
          {children}
        </CoreAccordionContent>
      )}
    </div>
  );
};

const CoreAccordionTrigger: React.FC<
  CoreAccordionItemProps & {
    clickHandler: (event: React.SyntheticEvent) => void;
    id: string;
  }
> = ({ title, isExpanded, leadingIcon, clickHandler, id }) => {
  const context = useContext(AccordionContext);

  const getAccordionIcon = (
    icon: CoreAccordionIconProps | undefined
  ): ReactNode => {
    if (icon?.name) {
      return (
        <CoreIcon
          name={icon?.name}
          size={context?.size === 'large' ? 'medium' : 'small'}
          ariaHidden
        />
      );
    }

    return null;
  };

  const TitleElement: 'h2' | 'h3' | 'h4' | 'h5' | 'h6' =
    context?.titleElement || 'h3';

  return (
    <TitleElement className={styles['core-accordion-heading']}>
      <button
        id={`${id}-trigger`}
        aria-controls={`${id}-content`}
        aria-expanded={isExpanded}
        className={styles['core-accordion-trigger']}
        onClick={clickHandler}
      >
        <span className={styles['core-accordion-trigger-heading']}>
          {getAccordionIcon(leadingIcon)}
          <span>{title}</span>
        </span>
        {isExpanded
          ? getAccordionIcon(context?.collapseIcon)
          : getAccordionIcon(context?.expandIcon)}
      </button>
    </TitleElement>
  );
};

const CoreAccordionContent: React.FC<{
  isExpanded: boolean;
  id: string;
  children: ReactNode;
}> = ({ isExpanded, id, children }) => {
  return (
    <div
      id={`${id}-content`}
      aria-labelledby={`${id}-trigger`}
      className={clsx(styles['core-accordion-content'], {
        [styles['core-accordion-content-collapsed']]: !isExpanded,
      })}
      role="region"
      aria-hidden={!isExpanded}
    >
      {children}
    </div>
  );
};

export default CoreAccordion;
